/**
 * PackBuilderStickyFooter — web component du bloc sticky footer.
 *
 * Responsabilités :
 *   • Ouvrir / fermer le drawer de sélection.
 *   • Mettre à jour les éléments UI du footer et du drawer quand la
 *     sélection change (`pack-builder:selection-changed`).
 *   • Ajouter la sélection au panier.
 *
 * Communication avec PackBuilder :
 *   • Lecture  → `pb.getSelection()`
 *   • Écriture → `pb.setVariantQty()`, `pb.clearSelection()`
 */
import { CartUpdatedEvent } from '@theme/events';
import { formatMoney } from '@theme/utilities';

/**
 * Remise % Shopify (centimes) : par unité, discount arrondi vers le bas.
 * @param {number} cents Prix unitaire en centimes
 * @param {number} percentOff
 */
const applyPercentOffCents = (cents, percentOff) => {
  const amount = Math.max(0, Math.round(Number(cents)) || 0);
  const pct = Number(percentOff);
  if (!Number.isFinite(pct) || pct <= 0) return amount;
  if (pct >= 100) return 0;
  const discount = Math.floor((amount * pct) / 100);
  return Math.max(0, amount - discount);
};

class PackBuilderStickyFooter extends HTMLElement {
  #abortController = null;
  #catalogCache = null;
  #stringsCache = null;
  #milestonesCache = null;
  #lastFocus = null;
  #desktopMediaQuery = null;

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  connectedCallback() {
    const { signal } = (this.#abortController = new AbortController());

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('pack-builder-subscription:update', this.#onPackSubscriptionUpdate, { signal });
    document.addEventListener('keydown', this.#onKeydown, { signal });
    window.addEventListener('pack-builder:selection-changed', this.#onSelectionChanged, { signal });
    this.#desktopMediaQuery = window.matchMedia('(min-width: 990px)');
    this.#desktopMediaQuery.addEventListener('change', this.#onViewportChanged, { signal });

    this.#updateUI();
    this.#syncDrawerMode();
    this.#syncBarCollapseMode();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  // ── Gestionnaires d'événements ────────────────────────────────────────────

  #onSelectionChanged = (event) => {
    const pb = this.closest('pack-builder');
    if (!pb) return;
    // Filtrer si plusieurs pack-builder sur la page
    if (event.detail?.sectionId && event.detail.sectionId !== pb.dataset.sectionId) return;
    this.#updateUI();
  };

  #onPackSubscriptionUpdate = (event) => {
    if (!this.contains(/** @type {Node} */ (event.target))) return;
    const d = event.detail;
    if (d == null || typeof d.pay !== 'number' || typeof d.compare !== 'number') return;

    // Totaux déjà avec remise palier (par unité, floor — aligné panier Shopify).
    this.#updatePrices(d.pay, Math.max(d.compare, d.pay));
  };

  #onKeydown = (event) => {
    if (this.#isDesktopLayout()) return;
    if (event.key !== 'Escape') return;
    if (this.querySelector('[data-pack-selection-drawer]')?.classList.contains('is-open')) {
      this.#closeDrawer();
    }
  };

  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    // Replier / déplier la barre (options abonnement) sur mobile
    const toggleBarBtn = target.closest('[data-pack-toggle-bar]');
    if (toggleBarBtn) {
      if (this.#isDesktopLayout()) return;
      this.#toggleBarCollapsed(toggleBarBtn);
      return;
    }

    // Ouvrir le drawer
    const openBtn = target.closest('[data-pack-open-drawer]');
    if (openBtn) {
      if (openBtn instanceof HTMLButtonElement && openBtn.disabled) return;
      if (this.#isDesktopLayout()) return;
      this.#openDrawer();
      return;
    }

    // Contrôles de quantité dans le drawer
    const qtyCtrl = target.closest('[data-pack-drawer-qty-dec],[data-pack-drawer-qty-inc]');
    if (qtyCtrl) {
      this.#handleDrawerQtyClick(qtyCtrl);
      return;
    }

    // Fermer le drawer (backdrop, bouton X, bouton Valider)
    if (target.closest('[data-pack-drawer-close]')) {
      if (this.#isDesktopLayout()) return;
      this.#closeDrawer();
      return;
    }

    // Ajouter la sélection au panier
    const addBtn = target.closest('[data-pack-add-selection]');
    if (addBtn) {
      if (addBtn instanceof HTMLButtonElement && addBtn.disabled) return;
      this.#addToCart(addBtn);
    }
  };

  // ── Contrôle de quantité dans le drawer ───────────────────────────────────

  #handleDrawerQtyClick(ctrl) {
    const row = ctrl.closest('[data-pack-drawer-line]');
    if (!(row instanceof HTMLElement)) return;

    const vid = row.dataset.variantId ?? '';
    if (!vid) return;

    const pb = this.closest('pack-builder');
    if (!pb) return;

    const sel = pb.getSelection();
    const line = sel.lines.find((l) => String(l.variantId) === vid);
    const current = line ? Math.max(0, parseInt(line.quantity, 10) || 0) : 0;

    if (ctrl.hasAttribute('data-pack-drawer-qty-dec')) {
      pb.setVariantQty(vid, Math.max(0, current - 1));
    } else {
      pb.setVariantQty(vid, current + 1);
    }
  }

  // ── Drawer ────────────────────────────────────────────────────────────────

  #onViewportChanged = () => {
    this.#syncDrawerMode();
    this.#syncBarCollapseMode();
  };

  #isDesktopLayout() {
    return this.#desktopMediaQuery?.matches ?? false;
  }

  #toggleBarCollapsed(btn) {
    const bar = this.querySelector('[data-pack-sticky-footer-bar]');
    if (!(bar instanceof HTMLElement)) return;

    const collapsed = bar.classList.toggle('is-collapsed');
    const expanded = !collapsed;

    if (btn instanceof HTMLElement) {
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      const label = expanded
        ? btn.getAttribute('data-collapse-label')
        : btn.getAttribute('data-expand-label');
      if (label) btn.setAttribute('aria-label', label);
    }
  }

  #syncBarCollapseMode() {
    const bar = this.querySelector('[data-pack-sticky-footer-bar]');
    const btn = this.querySelector('[data-pack-toggle-bar]');
    if (!(bar instanceof HTMLElement) || !(btn instanceof HTMLElement)) return;

    // Desktop : toujours déplié. Mobile : conserver l’état (défaut replié via Liquid).
    if (this.#isDesktopLayout()) {
      bar.classList.remove('is-collapsed');
      btn.setAttribute('aria-expanded', 'true');
      const label = btn.getAttribute('data-collapse-label');
      if (label) btn.setAttribute('aria-label', label);
      return;
    }

    const collapsed = bar.classList.contains('is-collapsed');
    btn.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    const label = collapsed
      ? btn.getAttribute('data-expand-label')
      : btn.getAttribute('data-collapse-label');
    if (label) btn.setAttribute('aria-label', label);
  }

  #syncDrawerMode() {
    const drawer = this.querySelector('[data-pack-selection-drawer]');
    if (!(drawer instanceof HTMLElement)) return;

    if (this.#isDesktopLayout()) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      return;
    }

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
  }

  #openDrawer() {
    if (this.#isDesktopLayout()) return;
    const drawer = this.querySelector('[data-pack-selection-drawer]');
    if (!(drawer instanceof HTMLElement)) return;

    this.#lastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    this.#updateUI();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');

    const panel = drawer.querySelector('[role="dialog"]');
    if (panel instanceof HTMLElement) panel.focus();
  }

  #closeDrawer() {
    if (this.#isDesktopLayout()) return;
    const drawer = this.querySelector('[data-pack-selection-drawer]');
    if (!(drawer instanceof HTMLElement)) return;

    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');

    if (this.#lastFocus) {
      this.#lastFocus.focus();
      this.#lastFocus = null;
    }
  }

  // ── Données JSON embarquées ───────────────────────────────────────────────

  #getCatalog() {
    if (this.#catalogCache != null) return this.#catalogCache;
    const el = this.querySelector('script[data-pack-variant-catalog]');
    try {
      this.#catalogCache = JSON.parse(el?.textContent?.trim() || '{}');
    } catch {
      this.#catalogCache = {};
    }
    return this.#catalogCache;
  }

  #getStrings() {
    if (this.#stringsCache != null) return this.#stringsCache;
    const el = this.querySelector('script[data-pack-footer-strings]');
    try {
      this.#stringsCache = JSON.parse(el?.textContent?.trim() || '{}');
    } catch {
      this.#stringsCache = {};
    }
    return this.#stringsCache;
  }

  #getMilestones() {
    if (this.#milestonesCache != null) return this.#milestonesCache;
    const el = this.querySelector('script[data-pack-tier-milestones]');
    try {
      const raw = JSON.parse(el?.textContent?.trim() || '[]');
      this.#milestonesCache = Array.isArray(raw)
        ? [...raw].sort((a, b) => (a.count || 0) - (b.count || 0))
        : [];
    } catch {
      this.#milestonesCache = [];
    }
    return this.#milestonesCache;
  }

  // ── Mise à jour UI globale ────────────────────────────────────────────────

  #updateUI() {
    const pb = this.closest('pack-builder');
    if (!pb) return;

    const sel = pb.getSelection();
    const catalog = this.#getCatalog();
    const strings = this.#getStrings();
    const milestones = this.#getMilestones();

    const selectedProductCount = sel.lines.length;
    const totalQuantity = sel.lines.reduce(
      (sum, line) => sum + Math.max(0, parseInt(line.quantity, 10) || 0),
      0,
    );
    const hasLines = selectedProductCount > 0;

    // Sous-totaux — remise palier par unité (floor) × qty, comme Shopify
    let pay = 0;
    let compare = 0;
    const percentOff = this.#activeTierDiscountPercent(totalQuantity);
    for (const line of sel.lines) {
      const entry = catalog[String(line.variantId)];
      const q = Math.max(0, parseInt(line.quantity, 10) || 0);
      if (!entry || q < 1) continue;
      const unit = typeof entry.price === 'number' ? Math.round(entry.price) : 0;
      const cmp = entry.compare_at_price;
      const unitCmp = typeof cmp === 'number' && cmp > unit ? Math.round(cmp) : unit;
      pay += applyPercentOffCents(unit, percentOff) * q;
      compare += unitCmp * q;
    }
    compare = Math.max(compare, pay);

    const packSubscription = this.querySelector('pack-builder-subscription');
    if (packSubscription) {
      queueMicrotask(() => {
        const pbs = this.querySelector('pack-builder-subscription');
        if (pbs && typeof pbs.requestFooterUpdate === 'function') {
          pbs.requestFooterUpdate();
          return;
        }
        this.#updatePrices(pay, compare);
      });
    } else {
      this.#updatePrices(pay, compare);
    }

    this.#updateBadges(selectedProductCount);
    this.#updateIncentive(totalQuantity, strings, milestones);
    this.#updateSummary(selectedProductCount, strings);
    this.#updateFoundationNotice(strings);
    this.#updatePreorderNotice(sel, catalog);
    this.#updateAddButtonLabel(sel, catalog, strings);
    this.#updateButtonStates(hasLines);
    this.#updateDrawerList(sel, catalog, strings);
  }

  #updatePrices(pay, compare) {
    for (const el of this.querySelectorAll('[data-pack-footer-total]')) {
      el.innerHTML = formatMoney(pay);
    }

    const compareWrap = this.querySelector('[data-pack-footer-compare-wrap]');
    const compareEl = this.querySelector('[data-pack-footer-compare]');
    if (compareWrap instanceof HTMLElement && compareEl instanceof HTMLElement) {
      if (compare > pay) {
        compareEl.innerHTML = formatMoney(compare);
        compareWrap.hidden = false;
      } else {
        compareWrap.hidden = true;
        compareEl.innerHTML = '';
      }
    }
  }

  #updateBadges(productCount) {
    for (const badge of this.querySelectorAll('[data-pack-modify-count]')) {
      badge.textContent = String(productCount);
    }
  }

  #formatTierDiscount(label) {
    return String(label ?? '')
      .trim()
      .replace(/^-/, '')
      .trim();
  }

  #fillIncentiveTemplate(tpl, replacements) {
    let result = tpl;
    for (const [key, value] of Object.entries(replacements)) {
      result = result.replace(new RegExp(`__${key}__`, 'g'), `<strong>${value}</strong>`);
    }
    return result;
  }

  #updateIncentive(totalQuantity, strings, milestones) {
    const statusEl = this.querySelector('[data-pack-incentive-status]');
    const nextEl = this.querySelector('[data-pack-incentive-next]');
    if (!(statusEl instanceof HTMLElement) || !(nextEl instanceof HTMLElement)) return;

    if (milestones.length === 0) {
      statusEl.hidden = true;
      nextEl.hidden = true;
      statusEl.innerHTML = '';
      nextEl.innerHTML = '';
      return;
    }

    const sorted = [...milestones].sort((a, b) => (a.count || 0) - (b.count || 0));
    const firstTier = sorted[0];
    const reached = [...sorted]
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .find((m) => totalQuantity >= (m.count || 0));
    const nextTier = sorted.find((m) => totalQuantity < (m.count || 0));

    if (totalQuantity === 0 && firstTier) {
      statusEl.innerHTML = this.#fillIncentiveTemplate(strings.incentive_minimum || '', {
        COUNT: String(firstTier.count || 0),
        DISCOUNT: this.#formatTierDiscount(firstTier.label),
      });
      statusEl.hidden = false;
      nextEl.hidden = true;
      nextEl.innerHTML = '';
      return;
    }

    if (reached) {
      statusEl.innerHTML = this.#fillIncentiveTemplate(strings.incentive_status || '', {
        DISCOUNT: this.#formatTierDiscount(reached.label),
      });
      statusEl.hidden = false;
    } else {
      statusEl.hidden = true;
      statusEl.innerHTML = '';
    }

    if (!nextTier) {
      if (reached) {
        nextEl.textContent = strings.incentive_complete || '';
        nextEl.hidden = false;
      } else {
        nextEl.hidden = true;
        nextEl.innerHTML = '';
      }
      return;
    }

    const remaining = Math.max(0, (nextTier.count || 0) - totalQuantity);
    const nextTpl =
      remaining === 1 ? strings.incentive_next_one || strings.incentive_next_plural : strings.incentive_next_plural;
    nextEl.innerHTML = this.#fillIncentiveTemplate(nextTpl || '', {
      REMAINING: String(remaining),
      DISCOUNT: this.#formatTierDiscount(nextTier.label),
    });
    nextEl.hidden = false;
  }

  #updateSummary(productCount, strings) {
    const el = this.querySelector('[data-pack-selection-summary]');
    if (!(el instanceof HTMLElement)) return;

    if (productCount === 0) {
      el.hidden = true;
      el.innerHTML = '';
      return;
    }

    const tpl = productCount === 1 ? strings.pack_contains_one || '' : strings.pack_contains_plural || '';
    el.innerHTML = tpl.replace(/__COUNT__/g, `<strong>${productCount}</strong>`);
    el.hidden = false;
  }

  #formatFoundationNoticeMessage(strings) {
    const pb = this.closest('pack-builder');
    const titles =
      pb && typeof pb.getFoundationProductTitles === 'function' ? pb.getFoundationProductTitles() : [];
    const joiner = strings.foundation_products_joiner || ' or ';
    const quoteOpen = strings.foundation_products_quote_open ?? '«';
    const quoteClose = strings.foundation_products_quote_close ?? '»';
    const products = titles.map((title) => `${quoteOpen}${title}${quoteClose}`).join(joiner);

    const customMessage = pb && typeof pb.getFoundationMessage === 'function' ? pb.getFoundationMessage() : '';
    if (customMessage.trim() !== '') {
      return customMessage.includes('__PRODUCTS__')
        ? customMessage.replace(/__PRODUCTS__/g, products)
        : customMessage;
    }

    return (strings.foundation_required || '').replace(/__PRODUCTS__/g, products);
  }

  #updateFoundationNotice(strings) {
    const el = this.querySelector('[data-pack-foundation-footer-notice]');
    const textEl = this.querySelector('[data-pack-foundation-footer-notice-text]');
    if (!(el instanceof HTMLElement) || !(textEl instanceof HTMLElement)) return;

    const pb = this.closest('pack-builder');
    const ruleEnabled = pb && typeof pb.isFoundationRuleEnabled === 'function' && pb.isFoundationRuleEnabled();
    const hasFoundation = pb && typeof pb.hasFoundationProduct === 'function' && pb.hasFoundationProduct();
    const show = Boolean(ruleEnabled && !hasFoundation);

    if (show) {
      textEl.textContent = this.#formatFoundationNoticeMessage(strings);
      el.hidden = false;
    } else {
      textEl.textContent = '';
      el.hidden = true;
    }
  }

  #isPreorderEntry(entry) {
    return entry?.preorder?.active === true;
  }

  #getPreorderPrecision(entry) {
    const prec = entry?.preorder?.precision;
    return typeof prec === 'string' && prec.trim() !== '' ? prec.trim() : null;
  }

  #getPreorderCartText(entry) {
    if (!this.#isPreorderEntry(entry)) return null;
    const text = entry?.preorder?.cart_text;
    return typeof text === 'string' && text.trim() !== '' ? text.trim() : null;
  }

  #getPreorderCartLabel(entry, strings) {
    const label = entry?.preorder?.cart_label;
    if (typeof label === 'string' && label.trim() !== '') return label.trim();
    const fallback = strings?.preorder_label;
    return typeof fallback === 'string' && fallback.trim() !== '' ? fallback.trim() : null;
  }

  #selectionIsAllPreorder(sel, catalog) {
    if (!sel?.lines?.length) return false;
    return sel.lines.every((line) => this.#isPreorderEntry(catalog[String(line.variantId)]));
  }

  #updatePreorderNotice(sel, catalog) {
    const el = this.querySelector('[data-pack-preorder-notice]');
    const textEl = this.querySelector('[data-pack-preorder-notice-text]');
    if (!(el instanceof HTMLElement) || !(textEl instanceof HTMLElement)) return;

    const precisions = [];
    const seen = new Set();

    for (const line of sel.lines) {
      const entry = catalog[String(line.variantId)];
      if (!this.#isPreorderEntry(entry)) continue;

      const prec = this.#getPreorderPrecision(entry);
      if (!prec || seen.has(prec)) continue;

      seen.add(prec);
      precisions.push(prec);
    }

    if (precisions.length === 0) {
      textEl.textContent = '';
      el.hidden = true;
      return;
    }

    textEl.textContent = precisions.join(' · ');
    el.hidden = false;
  }

  #updateAddButtonLabel(sel, catalog, strings) {
    const labelEl = this.querySelector('[data-pack-footer-add-label]');
    const btn = this.querySelector('[data-pack-add-selection]');
    if (!(labelEl instanceof HTMLElement) || !(btn instanceof HTMLButtonElement)) return;

    const defaultLabel = btn.dataset.packDefaultAddLabel || '';
    const allPreorder = this.#selectionIsAllPreorder(sel, catalog);
    labelEl.textContent = allPreorder && strings.preorder_add ? strings.preorder_add : defaultLabel;
  }

  #buildLineItemProperties(variantId, catalog, baseProps) {
    const props = { ...baseProps };
    const entry = catalog[String(variantId)];
    const cartText = this.#getPreorderCartText(entry);
    const propertyKey = this.#getPreorderCartLabel(entry, this.#getStrings());
    if (!cartText || !propertyKey) return props;

    props[propertyKey] = cartText;
    return props;
  }

  #updateButtonStates(hasLines) {
    const pb = this.closest('pack-builder');
    const foundationOk =
      !pb || typeof pb.isFoundationRuleEnabled !== 'function' || !pb.isFoundationRuleEnabled() || pb.hasFoundationProduct();

    for (const btn of this.querySelectorAll('[data-pack-open-drawer]')) {
      if (btn instanceof HTMLButtonElement) btn.disabled = !hasLines;
    }

    for (const btn of this.querySelectorAll('[data-pack-add-selection]')) {
      if (btn instanceof HTMLButtonElement) btn.disabled = !hasLines || !foundationOk;
    }
  }

  // ── Liste du drawer ───────────────────────────────────────────────────────

  #updateDrawerList(sel, catalog, strings) {
    const emptyEl = this.querySelector('[data-pack-drawer-empty]');
    const listEl = this.querySelector('[data-pack-drawer-list]');
    if (!(listEl instanceof HTMLUListElement)) return;

    listEl.innerHTML = '';
    for (const line of sel.lines) {
      listEl.appendChild(this.#buildDrawerLine(line, catalog, strings));
    }

    if (emptyEl instanceof HTMLElement) {
      const showEmpty = sel.lines.length === 0;
      emptyEl.hidden = !showEmpty;
      listEl.hidden = showEmpty;
    }
  }

  // ── Construction d'une ligne dans le drawer ───────────────────────────────

  #buildDrawerLine(line, catalog, strings) {
    const id = String(line.variantId);
    const entry = catalog[id] ?? {};
    const qty = Math.max(1, parseInt(line.quantity, 10) || 1);

    const li = document.createElement('li');
    li.className = 'pack-builder-selection-drawer__item';
    li.setAttribute('data-pack-drawer-line', '');
    li.dataset.variantId = id;

    li.append(
      this.#buildThumb(entry),
      this.#buildDetails(id, entry, qty, catalog),
      this.#buildQtyControl(qty, strings),
    );

    return li;
  }

  /** Vignette produit (image ronde). */
  #buildThumb(entry) {
    const wrap = document.createElement('div');
    wrap.className = 'pack-builder-selection-drawer__thumb';

    const src = typeof entry.image === 'string' && entry.image ? entry.image : null;
    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 112;
      img.height = 112;
      wrap.append(img);
    }

    return wrap;
  }

  /** Titre, options et prix d'une ligne. */
  #buildDetails(id, entry, qty, catalog) {
    const wrap = document.createElement('div');
    wrap.className = 'pack-builder-selection-drawer__details';

    // Titre produit
    const titleEl = document.createElement('p');
    titleEl.className = 'pack-builder-selection-drawer__product-title';
    titleEl.textContent =
      typeof entry.title === 'string' && entry.title ? entry.title : this.#lineTitle(id, catalog);
    wrap.append(titleEl);

    if (this.#isPreorderEntry(entry)) {
      const prec = this.#getPreorderPrecision(entry);
      if (prec) {
        const preorderLine = document.createElement('p');
        preorderLine.className = 'pack-builder-selection-drawer__preorder-line';

        const dot = document.createElement('span');
        dot.className = 'pack-builder-selection-drawer__preorder-line-dot';
        dot.setAttribute('aria-hidden', 'true');

        const text = document.createElement('span');
        text.textContent = prec;
        preorderLine.append(dot, text);
        wrap.append(preorderLine);
      }
    }

    // Options (couleur, taille…)
    const options = Array.isArray(entry.options) ? entry.options : [];
    const parts = options.filter((o) => o?.value && String(o.value).toLowerCase() !== 'default title').map((o) => ({ name: String(o.name || ''), value: String(o.value) }));

    if (parts.length > 0) {
      const meta = document.createElement('p');
      meta.className = 'pack-builder-selection-drawer__meta';

      parts.forEach((p, i) => {
        if (i > 0) meta.append(document.createTextNode('\u00a0\u00b7\u00a0'));

        const row = document.createElement('span');
        row.className = 'pack-builder-selection-drawer__meta-row';

        if (/color|colour|couleur|farbe/i.test(p.name)) {
          const dot = document.createElement('span');
          dot.className = 'pack-builder-selection-drawer__color-dot';
          dot.setAttribute('aria-hidden', 'true');
          const swatch = entry.color_swatch;
          if (typeof swatch === 'string' && swatch.trim() !== '') {
            dot.style.backgroundColor = swatch.trim();
          }
          row.append(dot);
        }

        const vt = document.createElement('span');
        vt.textContent = p.value;
        row.append(vt);
        meta.append(row);
      });

      wrap.append(meta);
    }

    // Prix de la ligne
    const unitPay = typeof entry.price === 'number' ? entry.price : 0;
    const cmp = entry.compare_at_price;
    const unitCmp = typeof cmp === 'number' && cmp > unitPay ? cmp : unitPay;
    const linePay = unitPay * qty;
    const lineCmp = unitCmp * qty;

    const pricesWrap = document.createElement('div');
    pricesWrap.className = 'pack-builder-selection-drawer__line-prices';

    if (lineCmp > linePay) {
      const del = document.createElement('del');
      del.className = 'pack-builder-selection-drawer__compare';
      del.textContent = formatMoney(lineCmp);
      pricesWrap.append(del);
    }

    const cur = document.createElement('p');
    cur.className = 'pack-builder-selection-drawer__price-current';
    cur.textContent = formatMoney(linePay);
    pricesWrap.append(cur);
    wrap.append(pricesWrap);

    return wrap;
  }

  /** Contrôles −/+ (avec icône poubelle quand quantité = 1). */
  #buildQtyControl(qty, strings) {
    const wrap = document.createElement('div');
    wrap.className = 'pack-builder-selection-drawer__qty';

    const showTrash = qty <= 1;

    const btnDec = document.createElement('button');
    btnDec.type = 'button';
    btnDec.className = 'pack-builder-selection-drawer__qty-btn pack-builder-selection-drawer__qty-btn--dec';
    if (showTrash) btnDec.classList.add('pack-builder-selection-drawer__qty-btn--remove-mode');
    btnDec.setAttribute('data-pack-drawer-qty-dec', '');
    btnDec.setAttribute('aria-label', showTrash ? strings.drawer_qty_remove || '' : strings.drawer_qty_decrease || '');
    btnDec.append(this.#matIcon(showTrash ? 'delete' : 'remove'));

    const qtyVal = document.createElement('span');
    qtyVal.className = 'pack-builder-selection-drawer__qty-value';
    qtyVal.textContent = String(qty);

    const btnInc = document.createElement('button');
    btnInc.type = 'button';
    btnInc.className = 'pack-builder-selection-drawer__qty-btn pack-builder-selection-drawer__qty-btn--inc';
    btnInc.setAttribute('data-pack-drawer-qty-inc', '');
    btnInc.setAttribute('aria-label', strings.drawer_qty_increase || '');
    btnInc.append(this.#matIcon('add'));

    wrap.append(btnDec, qtyVal, btnInc);
    return wrap;
  }

  // ── Utilitaires ───────────────────────────────────────────────────────────

  /**
   * Pourcentage de remise du palier actif (ex: 15 pour -15%).
   * Retourne 0 si aucun palier n'est atteint.
   */
  #activeTierDiscountPercent(totalQuantity) {
    const milestones = this.#getMilestones();
    const reached = [...milestones]
      .sort((a, b) => (b.count || 0) - (a.count || 0))
      .find((m) => totalQuantity >= (m.count || 0));

    if (!reached) return 0;

    const match = String(reached.label ?? '').match(/(\d+(?:\.\d+)?)\s*%/);
    if (!match) return 0;

    const pct = parseFloat(match[1]);
    if (!isFinite(pct) || pct <= 0 || pct >= 100) return 0;

    return pct;
  }

  /** Titre d'une ligne à partir du catalogue (fallback sur variantId). */
  #lineTitle(variantId, catalog) {
    const entry = catalog[variantId] ?? catalog[String(variantId)];
    if (!entry) return `Variant ${variantId}`;
    const vt = entry.variant_title;
    if (typeof vt === 'string' && vt && vt.toLowerCase() !== 'default title') {
      return `${entry.title} — ${vt}`;
    }
    return entry.title;
  }

  /** Crée un `<theme-icon>` Material Symbols (mask CSS). */
  #matIcon(name) {
    const el = document.createElement('theme-icon');
    el.className = `icon icon--${name}`;
    el.setAttribute('aria-hidden', 'true');

    const ref = document.querySelector('theme-icon[data-icon-style][data-icon-variant]');
    const iconStyle = ref?.dataset.iconStyle || 'rounded';
    const iconVariant = ref?.dataset.iconVariant || 'default';

    el.dataset.iconStyle = iconStyle;
    el.dataset.iconName = name;
    el.dataset.iconVariant = iconVariant;

    const opticalSize = 24;
    const url = `https://fonts.gstatic.com/s/i/short-term/release/materialsymbols${iconStyle}/${name}/${iconVariant}/${opticalSize}px.svg`;
    el.style.setProperty('--icon-url', `url('${url}')`);

    return el;
  }

  // ── Ajout au panier ───────────────────────────────────────────────────────

  async #addToCart(triggerBtn) {
    const pb = this.closest('pack-builder');
    if (!pb) return;

    const sel = pb.getSelection();
    if (!sel.lines.length) return;

    if (
      typeof pb.isFoundationRuleEnabled === 'function' &&
      pb.isFoundationRuleEnabled() &&
      typeof pb.hasFoundationProduct === 'function' &&
      !pb.hasFoundationProduct()
    ) {
      return;
    }

    // Fusionner les lignes dupliquées par variantId
    const byId = new Map();
    for (const line of sel.lines) {
      const key = String(line.variantId);
      const merged = byId.get(key) ?? { id: Number(line.variantId), quantity: 0 };
      merged.quantity += Math.max(1, parseInt(line.quantity, 10) || 1);
      byId.set(key, merged);
    }
    let items = Array.from(byId.values());

    const pbs = this.querySelector('pack-builder-subscription');
    if (pbs && typeof pbs.buildCartItems === 'function') {
      items = pbs.buildCartItems(items);
    }

    const catalog = this.#getCatalog();
    const baseProps = { '_pack-builder': 'true', '_test_migration': 'true' };

    items = items.map((item) => ({
      ...item,
      properties: {
        ...(typeof item.properties === 'object' && item.properties !== null ? item.properties : {}),
        ...this.#buildLineItemProperties(String(item.id), catalog, baseProps),
      },
    }));

    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer instanceof HTMLElement && cartDrawer.dataset.sectionId) {
      sections.push(cartDrawer.dataset.sectionId);
    }

    if (triggerBtn instanceof HTMLButtonElement) triggerBtn.classList.add('is-loading');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items, sections: sections.join(',') }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          (typeof data.description === 'string' && data.description) ||
            (typeof data.message === 'string' && data.message) ||
            response.statusText,
        );
      }

      const added = items.reduce((s, i) => s + i.quantity, 0);
      const currentCount = document.querySelector('cart-icon')?.getCurrentItemCount();
      const newCount = currentCount != null ? currentCount + added : undefined;

      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount: newCount, source: 'pack-builder', sections: data.sections }),
      );

      pb.clearSelection();
      this.#closeDrawer();

      document.dispatchEvent(
        new CustomEvent('toast:open', {
          bubbles: true,
          detail: { type: 'success', message: Theme.translations.addToCartNotificationMessage },
        }),
      );
    } catch (error) {
      console.error(error);
      document.dispatchEvent(
        new CustomEvent('toast:open', {
          bubbles: true,
          detail: { type: 'error', message: String(error?.message || error) },
        }),
      );
    } finally {
      if (triggerBtn instanceof HTMLButtonElement) triggerBtn.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('pack-builder-sticky-footer')) {
  customElements.define('pack-builder-sticky-footer', PackBuilderStickyFooter);
}
